import { useCallback, useEffect, useState } from 'react';
import { HelpCircle, RotateCcw } from 'lucide-react';
import { companionApi } from '../utils/companionApi.js';
import CardImage from './CardImage';
import { useT } from '../utils/i18n';

// "Which card is this?" from cards the family actually owns. Before the
// answer, only the illustration window of the card is shown: the name bar at
// the top and the rules text at the bottom are cropped away, so the picture
// cannot give the answer. After answering, the whole card is revealed.
// Per-session tally only; nothing persisted; no streaks.
export default function QuizCard({ initial = null, compact = false }) {
  const { t } = useT();
  const [q, setQ] = useState(initial);
  const [loading, setLoading] = useState(!initial);
  const [error, setError] = useState(null);
  const [picked, setPicked] = useState(null);
  const [tally, setTally] = useState({ right: 0, total: 0 });

  const load = useCallback(async () => {
    setLoading(true); setError(null); setPicked(null);
    try { setQ(await companionApi.quiz()); } catch (e) { setError(e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { if (!initial) load(); }, [initial, load]);

  const answer = (id) => {
    if (picked || !q) return;
    setPicked(id);
    setTally(tl => ({ right: tl.right + (id === q.answer_id ? 1 : 0), total: tl.total + 1 }));
  };

  if (error) return <div className="cmp-note cmp-note-error">{error.status === 409 || error.status === 404 ? t('companion.quiz.noCards') : t('companion.quiz.unavailable')}</div>;
  if (loading || !q) return <div className="cmp-note">{t('common.loading')}</div>;
  const correct = picked && picked === q.answer_id;
  const chosen = q.options?.find(o => o.id === picked);
  const right = q.options?.find(o => o.id === q.answer_id);
  const revealed = !!picked;
  return (
    <div className={`cmp-quiz ${compact ? 'cmp-quiz-compact' : ''}`}>
      <div className="cmp-quiz-head"><HelpCircle size={16} /> <span>{q.question || t('companion.quiz.whichOne')}</span></div>
      <div className="cmp-quiz-body">
        <div className={`cmp-quiz-art ${revealed ? 'revealed' : 'cropped'}`} aria-label={revealed ? (q.card?.name || '') : t('companion.quiz.artOnly')}>
          {/* Alt text is empty before the reveal on purpose: it would be the answer. */}
          <CardImage card={{ image_url: q.card?.image_url }} alt={revealed ? (q.card?.name || '') : ''} draggable={false} />
        </div>
        <div className="cmp-quiz-options" role="group" aria-label={t('companion.quiz.whichOne')}>
          {(q.options || []).map(o => (
            <button key={o.id} type="button" disabled={!!picked}
              className={`cmp-option ${picked ? (o.id === q.answer_id ? 'right' : o.id === picked ? 'wrong' : '') : ''}`}
              onClick={() => answer(o.id)}>{o.label}</button>
          ))}
        </div>
      </div>
      {picked && (
        <div className={`cmp-quiz-result ${correct ? 'ok' : ''}`}>
          {correct ? t('companion.quiz.correct', { name: right?.label || q.card?.name || '' }) : t('companion.quiz.wrong', { picked: chosen?.label || '', name: right?.label || q.card?.name || '' })}
          <div className="cmp-asof">{t('companion.quiz.source')}</div>
        </div>
      )}
      <div className="cmp-quiz-foot">
        <span>{tally.total > 0 ? t('companion.quiz.tally', { right: tally.right, total: tally.total }) : ''}</span>
        <button type="button" className="btn btn-secondary cmp-btn-sm" onClick={load}><RotateCcw size={14} /> {t('companion.quiz.another')}</button>
      </div>
    </div>
  );
}
